import { Box, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import React from 'react';
import ChatNotificationChannelIcon from '../../../components/admin/chatNotificationChannels/ChatNotificationChannelIcon';
import { Palette } from 'Palette';
import { StyledButton } from '../../../components/styles/StyledButtons';
import StyledSection from '../../../components/styles/StyledSection';
import ChatChannelCard from './ChatChannelCard';
import { useChatNotificationPreferences } from './useChatNotificationPreferences';

interface ChatAlertsSectionProps {
  chat: ReturnType<typeof useChatNotificationPreferences>;
}

const ChatAlertsSection: React.FC<ChatAlertsSectionProps> = ({ chat }) => {
  const {
    chatPrefs,
    chatPrefsLoading,
    chatPrefsSaving,
    chatPrefsHasChanges,
    googleChatConnecting,
    googleChatDisconnecting,
    teamsChatConnecting,
    teamsChatDisconnecting,
    slackChatConnecting,
    slackChatDisconnecting,
    addChannelMenuAnchor,
    setAddChannelMenuAnchor,
    updateChatPrefs,
    handleSaveChatPrefs,
    handleAddEmail,
    handleRemoveEmail,
    handleAddGoogleChat,
    handleRemoveGoogleChat,
    handleAddTeamsChat,
    handleRemoveTeamsChat,
    handleAddSlackChat,
    handleRemoveSlackChat,
  } = chat;

  if (chatPrefsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasAnyChannel =
    chatPrefs.emailEnabled ||
    chatPrefs.googleChatEnabled ||
    chatPrefs.teamsChatEnabled ||
    chatPrefs.slackChatEnabled;

  const canAddChannel =
    !chatPrefs.emailEnabled ||
    !chatPrefs.googleChatEnabled ||
    !chatPrefs.teamsChatEnabled ||
    !chatPrefs.slackChatEnabled;

  return (
    <StyledSection tone="tone1" child>
      {chatPrefs.emailEnabled && (
        <ChatChannelCard
          channel="email"
          label="Email"
          connected
          connectedDetail={null}
          timing={chatPrefs.emailTiming}
          unansweredMinutes={chatPrefs.emailUnansweredMinutes}
          saving={chatPrefsSaving}
          disconnecting={false}
          onRemove={handleRemoveEmail}
          onTimingChange={(emailTiming) => updateChatPrefs({ emailTiming })}
          onMinutesChange={(emailUnansweredMinutes) => updateChatPrefs({ emailUnansweredMinutes })}
        />
      )}

      {chatPrefs.googleChatEnabled && (
        <ChatChannelCard
          channel="googleChat"
          label="Google Chat"
          connected={chatPrefs.googleChatConnected}
          connectedDetail={
            chatPrefs.googleChatConnected
              ? `Connected${chatPrefs.googleUserEmail ? ` as ${chatPrefs.googleUserEmail}` : ''}`
              : null
          }
          timing={chatPrefs.googleChatTiming}
          unansweredMinutes={chatPrefs.googleChatUnansweredMinutes}
          saving={chatPrefsSaving}
          disconnecting={googleChatDisconnecting}
          connecting={googleChatConnecting}
          warningMessage="Authorise Fides to message you in Google Chat before alerts can be sent."
          authorizeLabel="Authorise Google Chat"
          onRemove={handleRemoveGoogleChat}
          onAuthorize={handleAddGoogleChat}
          onTimingChange={(googleChatTiming) => updateChatPrefs({ googleChatTiming })}
          onMinutesChange={(googleChatUnansweredMinutes) =>
            updateChatPrefs({ googleChatUnansweredMinutes })
          }
        />
      )}

      {chatPrefs.teamsChatEnabled && (
        <ChatChannelCard
          channel="teamsChat"
          label="Microsoft Teams"
          connected={chatPrefs.teamsChatConnected}
          connectedDetail={
            chatPrefs.teamsChatConnected
              ? `Connected${chatPrefs.teamsUserEmail ? ` as ${chatPrefs.teamsUserEmail}` : ''}`
              : null
          }
          timing={chatPrefs.teamsChatTiming}
          unansweredMinutes={chatPrefs.teamsChatUnansweredMinutes}
          saving={chatPrefsSaving}
          disconnecting={teamsChatDisconnecting}
          connecting={teamsChatConnecting}
          warningMessage="Sign in with Microsoft and install the Fides Teams app before alerts can be sent."
          authorizeLabel="Authorise Microsoft Teams"
          onRemove={handleRemoveTeamsChat}
          onAuthorize={handleAddTeamsChat}
          onTimingChange={(teamsChatTiming) => updateChatPrefs({ teamsChatTiming })}
          onMinutesChange={(teamsChatUnansweredMinutes) =>
            updateChatPrefs({ teamsChatUnansweredMinutes })
          }
        />
      )}

      {chatPrefs.slackChatEnabled && (
        <ChatChannelCard
          channel="slackChat"
          label="Slack"
          connected={chatPrefs.slackChatConnected}
          connectedDetail={
            chatPrefs.slackChatConnected
              ? `Connected${chatPrefs.slackUserEmail ? ` as ${chatPrefs.slackUserEmail}` : ''}`
              : null
          }
          timing={chatPrefs.slackChatTiming}
          unansweredMinutes={chatPrefs.slackChatUnansweredMinutes}
          saving={chatPrefsSaving}
          disconnecting={slackChatDisconnecting}
          connecting={slackChatConnecting}
          warningMessage="Authorise Fides to message you in Slack before alerts can be sent."
          authorizeLabel="Authorise Slack"
          onRemove={handleRemoveSlackChat}
          onAuthorize={handleAddSlackChat}
          onTimingChange={(slackChatTiming) => updateChatPrefs({ slackChatTiming })}
          onMinutesChange={(slackChatUnansweredMinutes) =>
            updateChatPrefs({ slackChatUnansweredMinutes })
          }
        />
      )}

      {!hasAnyChannel && (
        <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No notification channels added.</Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          pt: 2,
          pb: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {canAddChannel && (
          <>
            <StyledButton
              onClick={(e) => setAddChannelMenuAnchor(e.currentTarget)}
              disabled={
                chatPrefsSaving ||
                googleChatConnecting ||
                teamsChatConnecting ||
                slackChatConnecting
              }
            >
              + Add channel
            </StyledButton>
            <Menu
              anchorEl={addChannelMenuAnchor}
              open={Boolean(addChannelMenuAnchor)}
              onClose={() => setAddChannelMenuAnchor(null)}
            >
              {!chatPrefs.emailEnabled && (
                <MenuItem onClick={handleAddEmail}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <ChatNotificationChannelIcon channel="email" />
                    Email
                  </Box>
                </MenuItem>
              )}
              {!chatPrefs.googleChatEnabled && (
                <MenuItem onClick={handleAddGoogleChat}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <ChatNotificationChannelIcon channel="googleChat" />
                    Google Chat
                  </Box>
                </MenuItem>
              )}
              {!chatPrefs.teamsChatEnabled && (
                <MenuItem onClick={handleAddTeamsChat}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <ChatNotificationChannelIcon channel="teamsChat" />
                    Microsoft Teams
                  </Box>
                </MenuItem>
              )}
              {!chatPrefs.slackChatEnabled && (
                <MenuItem onClick={handleAddSlackChat}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <ChatNotificationChannelIcon channel="slackChat" />
                    Slack
                  </Box>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
        {chatPrefsHasChanges && (
          <StyledButton
            onClick={handleSaveChatPrefs}
            disabled={chatPrefsSaving}
            sx={{
              backgroundColor: Palette.default.buttonBg,
              color: Palette.default.buttonText,
              '&:hover': {
                backgroundColor: Palette.tone1.buttonBg,
                color: Palette.tone1.buttonText,
              },
            }}
          >
            {chatPrefsSaving ? <CircularProgress size={20} /> : 'Save timing'}
          </StyledButton>
        )}
      </Box>
    </StyledSection>
  );
};

export default ChatAlertsSection;
